
import languagesList from '@/resources/languagesList';
import requestAPI from '@/logic/requestBeApi';
import arrDiff from '@/logic/arrayDiff';
import store from '@/store';
import helpers from '@/logic/helpers/forms/onboardingHelpers';
import { refreshTokenHandler } from '@/logic/auth';
import inputValidators from '@/logic/helpers/inputs/validators';
import ServerResponseAlert from '@/components/ServerResponseAlert.vue';
import passDataToResponseAlert from '@/logic/helpers/api/passDataToResponseAlert';

export default {
  name: 'AssessorOnboardingExpertise',

  components: {
    ServerResponseAlert,
  },

  props: {
    isPartOfProfile: Boolean,
    assessorExpertises: {
      type: Object,
      default: (): Record<string, never> => ({}),
    },
    focusOnProgressCircle: Boolean,
  },

  data(): Record<string, unknown> {
    return {
      onboardingExpertise: {
        assessmentTypes: [],
        tools: [],
        languages: [],
        levels: [],
        functions: [],
        industries: [],
        countries_performed_in: [],
        nationalities_assessed: [],
        companies: [],
      },
      validationRules: {
        assessmentTypes: [{ validator: inputValidators.structural, trigger: 'change' }],
        tools: [{ validator: inputValidators.structural, trigger: 'change' }],
        languages: [{ validator: inputValidators.structural, trigger: 'change' }],
        levels: [{ validator: inputValidators.structural, trigger: 'change' }],
        functions: [{ validator: inputValidators.structural, trigger: 'change' }],
        industries: [{ validator: inputValidators.structural, trigger: 'change' }],
      },
      expTypes: [],
      processingUserDataRequest: false,
      expAssessmentTypes: { watchCount: 0, pending: false, slug: 'assessment-types' },
      expTools: { watchCount: 0, pending: false, slug: 'tools' },
      expLanguages: { watchCount: 0, pending: false, slug: 'languages' },
      expLevels: { watchCount: 0, pending: false, slug: 'levels' },
      expFunctions: { watchCount: 0, pending: false, slug: 'functions' },
      expIndustries: { watchCount: 0, pending: false, slug: 'industries' },
      assessmentTypesList: [],
      toolsList: [],
      languagesList,
      levelsList: [],
      functionsList: [],
      industriesList: [],
      serverResponse: null,
      saveAndQuit: false,
      formIsValid: false,
    };
  },

  computed: {
    hasEveryTypeOfExpertise(): boolean {
      return (
        !!this.onboardingExpertise.assessmentTypes.length &&
        !!this.onboardingExpertise.tools.length &&
        !!this.onboardingExpertise.languages.length &&
        !!this.onboardingExpertise.levels.length &&
        !!this.onboardingExpertise.functions.length &&
        !!this.onboardingExpertise.industries.length
      );
    },

    assessmentTypes(): string[] {
      return this.onboardingExpertise.assessmentTypes;
    },

    tools(): string[] {
      return this.onboardingExpertise.tools;
    },

    languages(): string[] {
      return this.onboardingExpertise.languages;
    },

    levels(): string[] {
      return this.onboardingExpertise.levels;
    },

    functions(): string[] {
      return this.onboardingExpertise.functions;
    },

    industries(): string[] {
      return this.onboardingExpertise.industries;
    },
  },

  watch: {
    assessorExpertises(newVal: unknown): void {
      if (this.isPartOfProfile && newVal) {
        this.assignExpertises(newVal);
      }
    },

    assessmentTypes(newVal: unknown, oldVal: unknown): void {
      this.handleExpertiseChange('expAssessmentTypes', newVal, oldVal);
    },

    tools(newVal: unknown, oldVal: unknown): void {
      this.handleExpertiseChange('expTools', newVal, oldVal);
    },

    languages(newVal: unknown, oldVal: unknown): void {
      this.handleExpertiseChange('expLanguages', newVal, oldVal);
    },

    levels(newVal: unknown, oldVal: unknown): void {
      this.handleExpertiseChange('expLevels', newVal, oldVal);
    },

    functions(newVal: unknown, oldVal: unknown): void {
      this.handleExpertiseChange('expFunctions', newVal, oldVal);
    },

    industries(newVal: unknown, oldVal: unknown): void {
      this.handleExpertiseChange('expIndustries', newVal, oldVal);
    },

    hasEveryTypeOfExpertise(): void {
      this.hasEveryTypeOfExpertise
        ? this.$emit('hasEveryTypeOfExpertise')
        : this.$emit('doesntHaveEveryTypeOfExpertise');
    },
  },

  created(): void {
    this.getExpertiseTypes();
    if (!this.isPartOfProfile && store.state.userLang) {
      this.language = store.state.userLang;
    }
  },

  methods: {
    submitForm(saveOption: 'saveAndQuit' | 'saveAndContinue'): void {
      helpers.validateAndSubmitUserData(this, 'onboardingExpertiseForm', saveOption);
    },

    handleExpertiseChange(expertise: string | number, newVal: unknown, oldVal: unknown): void {
      this.$refs[expertise].blur();
      this.onExpertiseChange(this[expertise], newVal, oldVal);
    },

    assignExpertises(assessorExpertises: {
      assessmentTypes: unknown[];
      tools: unknown[];
      languages: unknown[];
      levels: unknown[];
      functions: unknown[];
      industries: unknown[];
    }): void {
      this.onboardingExpertise.assessmentTypes = assessorExpertises.assessmentTypes.map(
        (obj: { name: string }) => obj.name,
      );
      this.onboardingExpertise.tools = assessorExpertises.tools.map(
        (obj: { name: string }) => obj.name,
      );
      this.onboardingExpertise.languages = assessorExpertises.languages.map(
        (obj: { name: string }) => this.getLangFullName(obj.name),
      );
      this.onboardingExpertise.levels = assessorExpertises.levels.map(
        (obj: { name: string }) => obj.name,
      );
      this.onboardingExpertise.functions = assessorExpertises.functions.map(
        (obj: { name: string }) => obj.name,
      );
      this.onboardingExpertise.industries = assessorExpertises.industries.map(
        (obj: { name: string }) => obj.name,
      );
    },

    getExpertiseIdLink(expSlug: string): string {
      const expertise = this.expTypes.find((expType: { slug: string }) => expType.slug === expSlug);

      return `/api/v1/expertise-types/${expertise.id}`;
    },

    async onExpertiseChange(
      expertise: { watchCount: number; pending: boolean; slug: string },
      newVal: string[] | unknown[],
      oldVal: string[] | unknown[],
    ): Promise<void> {
      expertise.watchCount++;

      if (!this.isPartOfProfile || expertise.watchCount === 1) {
        return;
      }

      expertise.pending = true;

      let action: string;
      let resp: { ok: any; json: () => any };
      const expertiseVal = arrDiff(newVal, oldVal)[0];

      newVal.length > oldVal.length ? (action = 'added') : (action = 'deleted');

      if (action === 'added') {
        const expValAlredyExists = this.getUUId(expertise.slug, expertiseVal) ? true : false;

        if (expValAlredyExists) {
          const UUId = this.getUUId(expertise.slug, expertiseVal);
          resp = await requestAPI({ method: 'PATCH', url: `/api/v1/expertises/${UUId}/link` });
        } else {
          const payload = {
            name: expertiseVal,
            expertise_type: {
              id: this.getExpertiseIdLink(expertise.slug),
            },
            description: '',
            eazy_expertise: true,
            experience: 0,
          };
          resp = await requestAPI({ method: 'POST', url: `/api/v1/expertises`, payload });
        }
      } else if (action === 'deleted') {
        const UUId = this.getUUId(expertise.slug, expertiseVal);

        if (!UUId) {
          expertise.pending = false;
          return;
        }

        resp = await requestAPI({ method: 'DELETE', url: `/api/v1/expertises/${UUId}/unlink` });
      }

      if (resp.ok) {
        if (expertise.slug === 'languages' && action === 'added') {
          const data = await resp.json();
          this.addIdToLanguages(expertiseVal, data.id);
        }

        if (!this.focusOnProgressCircle) {
          this.$message.success(this.$t('expertiseInfoUpdated'));
        }

        this.$emit('runGetProfileCompletionProcess');

        if (resp.ok) {
          this.makeRefreshTokenCall();
        }
      } else {
        this.$message({
          message: this.$t('someErrorOccurred'),
          type: 'error',
        });
      }

      expertise.pending = false;
    },

    addIdToLanguages(name: string, id: string): void {
      const expertise = this.expTypes.find(
        (expType: { slug: string }) => expType.slug === 'languages',
      );
      expertise.expertises.push({
        name,
        id,
      });
    },

    getUUId(expSlug: string, expVal: string): void {
      const expertise = this.expTypes.find((expType: { slug: string }) => expType.slug === expSlug);

      try {
        if (expSlug === 'languages' && expVal.length > 2) {
          expVal = this.languagesList.find((lan: { name: string }) => lan.name === expVal).code;
        }

        const uuid = expertise.expertises.find(
          (exp: { name: string }) => exp.name.toLowerCase() === expVal.toLowerCase(),
        ).id;

        return uuid;
      } catch {
        return undefined;
      }
    },

    async getExpertiseTypes(): Promise<void> {
      this.processingUserDataRequest = true;

      const resp = await requestAPI({
        url: '/api/v1/expertise-types?order%5Bexpertises.name%5D=ASC',
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();

        this.expTypes = data;
        this.toolsList = data.find((obj: { slug: string }) => obj.slug === 'tools').expertises;
        this.levelsList = data.find((obj: { slug: string }) => obj.slug === 'levels').expertises;
        this.functionsList = data.find(
          (obj: { slug: string }) => obj.slug === 'functions',
        ).expertises;
        this.industriesList = data.find(
          (obj: { slug: string }) => obj.slug === 'industries',
        ).expertises;
        this.assessmentTypesList = data.find(
          (obj: { slug: string }) => obj.slug === 'assessment-types',
        ).expertises;
      }

      this.processingUserDataRequest = false;
    },

    async submitUserData(): Promise<void> {
      this.serverResponse = {};
      this.processingUserDataRequest = true;

      const payload = this.onboardingExpertise;

      const onboardingExpertiseResponse = await requestAPI({
        method: 'POST',
        url: '/api/v1/onboard/expertise',
        payload,
      });

      if (onboardingExpertiseResponse.ok) {
        const makeImmediateRefreshTokenCall = true;
        const clearOldRefrTokenTimeout = true;

        refreshTokenHandler(makeImmediateRefreshTokenCall, clearOldRefrTokenTimeout);

        this.saveAndQuit
          ? helpers.endOnboarding()
          : this.$emit('setStep', 'business_conditions', onboardingExpertiseResponse);
      } else {
        this.saveAndQuit = false;
        const data = await passDataToResponseAlert(onboardingExpertiseResponse);
        this.serverResponse = data;
      }

      this.processingUserDataRequest = false;
    },

    getLangFullName(name: string): string {
      return this.languagesList.find((lang: { code: string }) => lang.code === name.toLowerCase())
        .name;
    },

    goNextScreen(): void {
      this.$emit('setStep', 'business_conditions');
    },

    makeRefreshTokenCall(): void {
      const makeImmediateRefreshTokenCall = true;
      const clearOldRefrTokenTimeout = true;

      refreshTokenHandler(makeImmediateRefreshTokenCall, clearOldRefrTokenTimeout);
    },
  },
};
